import { InternalMessage } from '@slices/userMessage'

export type LatestInferenceMessage = {
  userInput?: unknown
  response?: unknown
}

export type InferenceStatus = 'idle' | 'submitting' | 'streaming'

export type InferenceState = {
  currentSessionId?: string
  status: InferenceStatus
  latestMessage: LatestInferenceMessage
  videoEnabled: boolean
  videoLink: string
  videoLoading: boolean
}

export type InferenceAction =
  | { type: 'setCurrentSessionId'; sessionId?: string }
  | { type: 'setLatestMessage'; latestMessage: LatestInferenceMessage }
  | { type: 'setSubmitting'; submitting: boolean }
  | { type: 'setStreaming' }
  | { type: 'setVideoEnabled'; enabled: boolean }
  | { type: 'setVideoLink'; videoLink: string }
  | { type: 'setVideoLoading'; loading: boolean }

export const createInitialInferenceState = (sessionId?: string): InferenceState => ({
  currentSessionId: sessionId,
  status: 'idle',
  latestMessage: {},
  videoEnabled: false,
  videoLink: '',
  videoLoading: false,
})

export function inferenceReducer(state: InferenceState, action: InferenceAction): InferenceState {
  switch (action.type) {
    case 'setCurrentSessionId':
      return { ...state, currentSessionId: action.sessionId }
    case 'setLatestMessage':
      return { ...state, latestMessage: action.latestMessage }
    case 'setSubmitting':
      return { ...state, status: action.submitting ? 'submitting' : 'idle' }
    case 'setStreaming':
      return { ...state, status: 'streaming' }
    case 'setVideoEnabled':
      return { ...state, videoEnabled: action.enabled }
    case 'setVideoLink':
      return { ...state, videoLink: action.videoLink }
    case 'setVideoLoading':
      return { ...state, videoLoading: action.loading }
    default:
      return state
  }
}

const LOADING_QUEUE_STATUSES = new Set<NonNullable<InternalMessage['queueStatus']>>([
  'receiving',
  'processing_files',
  'queued',
  'dispatching',
  'running',
  'waiting_for_approval',
  'waiting_for_background',
])

export const selectInferenceLoading = (state: InferenceState, messages: InternalMessage[] = []) =>
  state.status === 'submitting' ||
  state.status === 'streaming' ||
  messages.some(message => message.queueStatus && LOADING_QUEUE_STATUSES.has(message.queueStatus))
